/**
 * Session Initializer Component
 *
 * Initializes PostHog session tracking when the app loads
 * Captures entry metadata (wallet provider, referrer, etc.)
 * and sets up user properties for the session
 *
 * Also handles user identification when profile data is available
 */

import { useSearch } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { STEP_LABELS, useAnalytics } from "../hooks/usePostHog";
import { normalizeOperation } from "../lib/normalize-operation";
import { resolveStableDistinctId } from "../lib/resolve-stable-distinct-id";
import { useRampsStore } from "../stores/use-ramps-store";
import { stellarAddressFromJwtSub } from "../utils/jwt-decoder";

export function SessionInitializer() {
	const {
		trackSessionStart,
		trackExit,
		identifyUser,
		aliasUser,
		partnerInfo,
		setUserProperties,
		posthog,
	} = useAnalytics();
	const search = useSearch({ strict: false });
	const store = useRampsStore();
	const hasIdentified = useRef(false);
	const previousDistinctId = useRef<string | null>(null);
	const sessionStartedRef = useRef(false);
	/** wallet_provider last written to the person record, to avoid redundant writes. */
	const identifiedProvider = useRef<string | null>(null);

	useEffect(() => {
		const handleBeforeUnload = () => {
			const urlParams = new URLSearchParams(window.location.search);
			const currentStep = urlParams.get("step") || "calculator";

			const stepProgress: Record<string, number> = {
				calculator: 10,
				phone: 25,
				name: 40,
				address: 50,
				"date-of-birth": 60,
				identification: 70,
				"digital-offramp-info": 75,
				"additional-info": 80,
				review: 90,
				status: 100,
			};

			const completionPercentage = stepProgress[currentStep] || 0;

			if (currentStep !== "status") {
				trackExit("abandoned", {
					exit_reason: "browser_close_or_navigation",
					abandonment_step: STEP_LABELS[currentStep] || currentStep,
					completion_percentage: completionPercentage,
					pages_visited: getVisitedPagesCount(),
				});
			}
		};

		window.addEventListener("beforeunload", handleBeforeUnload);

		return () => {
			window.removeEventListener("beforeunload", handleBeforeUnload);
		};
	}, [trackExit]);

	useEffect(() => {
		const searchParams = search as Record<string, unknown>;
		const operation = normalizeOperation(
			(searchParams?.operation as string) || "withdraw",
		);

		const hasDigitalProfile = !!store.digitalProfileId;
		const isReturningUser =
			hasDigitalProfile &&
			(store.walletLookupCompleted || store.phoneLookupCompleted);
		const isNewProfileCreated = store.profileCreationCompleted;

		const forensicMeta = !partnerInfo.wallet_provider_known
			? {
					wallet_provider_raw: partnerInfo.wallet_provider_raw,
					entry_referrer:
						typeof document !== "undefined"
							? document.referrer || undefined
							: undefined,
					entry_url:
						typeof window !== "undefined"
							? window.location.origin + window.location.pathname
							: undefined,
					token_type: searchParams?.accesstoken
						? "oauth"
						: searchParams?.token
							? "jwt"
							: "none",
				}
			: {};

		const stable = resolveStableDistinctId(search, {
			token: store.token,
			walletAddress: store.walletAddress,
		});

		const maybeAliasFromCurrentDistinctId = (canonicalId: string) => {
			const current = posthog?.get_distinct_id?.();
			if (current && current !== canonicalId) {
				aliasUser(current, canonicalId);
			}
		};

		if (!hasIdentified.current) {
			if (store.digitalProfileId) {
				const profileDistinctId = `profile_${store.digitalProfileId}`;
				maybeAliasFromCurrentDistinctId(profileDistinctId);
				identifyUser(profileDistinctId, {
					digital_profile_id: store.digitalProfileId,
					has_digital_profile: true,
					is_returning_user: isReturningUser,
					profile_created_this_session: isNewProfileCreated,
					transaction_id: store.transactionId,
					wallet_address: store.walletAddress || undefined,
					wallet_provider: partnerInfo.wallet_provider,
					wallet_provider_known: partnerInfo.wallet_provider_known,
					operation: operation,
					phone_number: store.phone,
					email: store.email,
					name: `${store.firstName} ${store.lastName}`.trim() || undefined,
					language: (searchParams?.lang as string) || "en",
					api_version: store.apiVersion || "v3",
					wallet_lookup_completed: store.walletLookupCompleted,
					phone_lookup_completed: store.phoneLookupCompleted,
					$set_once: { first_seen_at: new Date().toISOString() },
					...forensicMeta,
				});
				hasIdentified.current = true;
				previousDistinctId.current = null;
			} else if (stable) {
				const sid = stable.distinctId;
				maybeAliasFromCurrentDistinctId(sid);
				const walletAddressProp = stable.fromJwt
					? stellarAddressFromJwtSub(stable.distinctId)
					: stable.distinctId;
				identifyUser(sid, {
					auth_type: stable.auth_type,
					transaction_id: store.transactionId,
					wallet_address: walletAddressProp,
					has_digital_profile: false,
					is_returning_user: false,
					wallet_provider: partnerInfo.wallet_provider,
					wallet_provider_known: partnerInfo.wallet_provider_known,
					operation: operation,
					language: (searchParams?.lang as string) || "en",
					api_version: store.apiVersion || "v3",
					...forensicMeta,
				});
				hasIdentified.current = true;
				previousDistinctId.current = sid;
			} else if (store.transactionId) {
				const txDistinctId = `tx_${store.transactionId}`;
				previousDistinctId.current = txDistinctId;
				identifyUser(txDistinctId, {
					transaction_id: store.transactionId,
					has_digital_profile: false,
					is_returning_user: false,
					wallet_provider: partnerInfo.wallet_provider,
					wallet_provider_known: partnerInfo.wallet_provider_known,
					operation: operation,
					language: (searchParams?.lang as string) || "en",
					api_version: store.apiVersion || "v3",
					...forensicMeta,
				});
				hasIdentified.current = true;
			} else if (store.phone || store.email) {
				const userId = store.phone
					? `phone_${store.phone}`
					: store.email
						? `email_${store.email}`
						: `session_${Date.now()}`;

				previousDistinctId.current = userId;
				identifyUser(userId, {
					phone_number: store.phone,
					email: store.email,
					has_digital_profile: false,
					wallet_provider: partnerInfo.wallet_provider,
					wallet_provider_known: partnerInfo.wallet_provider_known,
					operation: operation,
					language: (searchParams?.lang as string) || "en",
					api_version: store.apiVersion || "v3",
					...forensicMeta,
				});
				hasIdentified.current = true;
			}

			if (hasIdentified.current) {
				identifiedProvider.current = partnerInfo.wallet_provider;
			}
		} else if (store.digitalProfileId && hasIdentified.current) {
			const profileDistinctId = `profile_${store.digitalProfileId}`;

			if (
				previousDistinctId.current &&
				previousDistinctId.current !== profileDistinctId
			) {
				aliasUser(previousDistinctId.current, profileDistinctId);
				previousDistinctId.current = null;
			}

			identifyUser(profileDistinctId, {
				digital_profile_id: store.digitalProfileId,
				has_digital_profile: true,
				is_returning_user: isReturningUser,
				profile_created_this_session: isNewProfileCreated,
				transaction_id: store.transactionId,
				wallet_address: store.walletAddress,
				wallet_provider: partnerInfo.wallet_provider,
				wallet_provider_known: partnerInfo.wallet_provider_known,
				phone_number: store.phone,
				email: store.email,
				wallet_lookup_completed: store.walletLookupCompleted,
				phone_lookup_completed: store.phoneLookupCompleted,
				$set_once: { first_seen_at: new Date().toISOString() },
			});
			identifiedProvider.current = partnerInfo.wallet_provider;
		} else if (
			partnerInfo.wallet_provider_known &&
			identifiedProvider.current !== partnerInfo.wallet_provider
		) {
			// Attribution can land after identification when the JWT carries no
			// client_name and /transactions/prepare supplies the partner instead.
			// Events pick that up on their own (super properties re-register), but the
			// person record would keep the stale value. Update the properties rather
			// than re-identifying: the distinct_id is unchanged, and calling identify
			// again would risk another alias on an already-noisy person graph.
			identifiedProvider.current = partnerInfo.wallet_provider;
			setUserProperties({
				wallet_provider: partnerInfo.wallet_provider,
				wallet_provider_known: true,
			});
		}

		if (!sessionStartedRef.current) {
			sessionStartedRef.current = true;
			trackSessionStart({});
		}
	}, [
		search,
		store.digitalProfileId,
		store.transactionId,
		store.phone,
		store.email,
		store.firstName,
		store.lastName,
		store.walletAddress,
		store.token,
		store.apiVersion,
		store.profileCreationCompleted,
		store.walletLookupCompleted,
		store.phoneLookupCompleted,
		partnerInfo,
		trackSessionStart,
		identifyUser,
		aliasUser,
		setUserProperties,
		posthog,
	]);

	return null;
}

function getVisitedPagesCount(): number {
	try {
		const visited = sessionStorage.getItem("ramps_pages_visited");
		return visited ? JSON.parse(visited).length : 1;
	} catch {
		return 1;
	}
}
